import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import { AuthCallback } from "@/pages/AuthCallback";
import { ProtectedRoute } from "@/pages/ProtectedRoute";

import PricingPage from "@/pages/marketing/PricingPage";
import VoicesPage from "@/pages/marketing/VoicesPage";
import WordPressPage from "@/pages/marketing/WordPressPage";
import AboutPage from "@/pages/marketing/AboutPage";
import PrivacyPage from "@/pages/marketing/PrivacyPage";
import TermsPage from "@/pages/marketing/TermsPage";
import CookiesPage from "@/pages/marketing/CookiesPage";
import PublicSupportPage from "@/pages/marketing/PublicSupportPage";
import NotAvailablePage from "@/pages/marketing/NotAvailablePage";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Articles from "@/pages/dashboard/Articles";
import ArticleNew from "@/pages/dashboard/ArticleNew";
import ArticleMultiVoice from "@/pages/dashboard/ArticleMultiVoice";
import VoicesDashboard from "@/pages/dashboard/VoicesDashboard";
import Embed from "@/pages/dashboard/Embed";
import Analytics from "@/pages/dashboard/Analytics";
import Billing from "@/pages/dashboard/Billing";
import PaymentCheckout from "@/pages/dashboard/PaymentCheckout";
import PaymentCallback from "@/pages/dashboard/PaymentCallback";
import Team from "@/pages/dashboard/Team";
import SettingsPage from "@/pages/dashboard/Settings";
import Support from "@/pages/dashboard/Support";
import Monetize from "@/pages/dashboard/Monetize";
import ApiPage from "@/pages/dashboard/ApiPage";
import AdminPanel from "@/pages/admin/AdminPanel";
import WordPressAuth from "@/pages/WordPressAuth";

function AppRouter() {
    const location = useLocation();
    // CRITICAL: Detect OAuth callback synchronously during render.
    // useEffect would run too late and lose the session_id race.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback/>;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing/>}/>
            <Route path="/login" element={<Login/>}/>
            <Route path="/register" element={<Register/>}/>
            <Route path="/verify-email" element={<VerifyEmail/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/reset-password" element={<ResetPassword/>}/>

            <Route path="/pricing" element={<PricingPage/>}/>
            <Route path="/voices" element={<VoicesPage/>}/>
            <Route path="/wordpress" element={<WordPressPage/>}/>
            <Route path="/about" element={<AboutPage/>}/>
            <Route path="/privacy" element={<PrivacyPage/>}/>
            <Route path="/terms" element={<TermsPage/>}/>
            <Route path="/cookies" element={<CookiesPage/>}/>
            <Route path="/support" element={<PublicSupportPage/>}/>
            <Route path="/not-available" element={<NotAvailablePage/>}/>

            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout/></ProtectedRoute>}>
                <Route index element={<Overview/>}/>
                <Route path="articles" element={<Articles/>}/>
                <Route path="articles/new" element={<ArticleNew/>}/>
                <Route path="articles/new-multi" element={<ArticleMultiVoice/>}/>
                <Route path="voices" element={<VoicesDashboard/>}/>
                <Route path="embed" element={<Embed/>}/>
                <Route path="analytics" element={<Analytics/>}/>
                <Route path="billing" element={<Billing/>}/>
                <Route path="support" element={<Support/>}/>
                <Route path="team" element={<Team/>}/>
                <Route path="settings" element={<SettingsPage/>}/>
                <Route path="monetize" element={<Monetize/>}/>
                <Route path="api" element={<ApiPage/>}/>
            </Route>

            <Route path="/admin" element={<ProtectedRoute><AdminPanel/></ProtectedRoute>}/>
            <Route path="/checkout" element={<ProtectedRoute><PaymentCheckout/></ProtectedRoute>}/>
            <Route path="/payment/callback" element={<PaymentCallback/>}/>
            <Route path="/wordpress-auth" element={<WordPressAuth/>}/>

            {/* 404 — back to landing */}
            <Route path="*" element={<Landing/>}/>
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRouter/>
                <Toaster position="bottom-center" theme="light"/>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
